import { GenericPageError } from '@components/core-ui/GenericPageError';
import { resolveOrganizationBillingAccountId } from '@helpers/resolveOrganizationBillingAccountId';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';

import { useAuthContext } from '@providers/AuthProvider';
import { useModalContext } from '@providers/ModalProvider';
import { routes } from '@routes';
import InfoIcon from 'app/components/icons/Info.svg';
import { Fragment } from 'react';
import { useNavigate } from 'react-router';
import { ErrorClickableSpan } from './styles/ErrorClickableSpan';
import { InfoWrapper } from './styles/InfoWrapper';
import { TProps } from './types/TProps';

export { InfoWrapper };

export function PriceModalErrors({
  setBalanceIsRefreshing,
  price,
  onCancel,
  setPaymentIntent,
  paymentIntent,
  dataLoaded,
  setDataLoaded,
  onSuccess,
  jobId,
  isSubmitting,
  paymentMethodId,
}: TProps) {
  const { openModal } = useModalContext();
  const { organization, updateMe } = useAuthContext();

  const navigate = useNavigate();

  const balance = organization?.billingAccount?.currentBalance ?? 0;
  const notEnoughFunds = (price?.price ?? 0) > balance;
  const cannotManageBilling = !organization?.permissions?.manageBilling;
  const cannotViewBilling = !organization?.permissions?.viewBilling;
  const noBillingAccount = !resolveOrganizationBillingAccountId(organization);
  const autoTopUpEnabled = organization?.billingAccount?.AutoTopUpSettings?.enabled;

  const openAddFundsModal = () => {
    openModal(ModalNamesEnum.TopUpCreditBalance, {
      onCancel,
      initialAmount: Math.ceil(((price?.price ?? 0) - balance) / 100),
      onSuccess: () => {
        openModal(ModalNamesEnum.PriceModal, {
          onCancel,
          isStripeLoading: false,
          setPaymentIntent,
          paymentIntent,
          onSuccess: (model: string) => {
            setDataLoaded(false);
            onSuccess(model);
            setBalanceIsRefreshing(true);
            setTimeout(() => {
              updateMe()
                .then(() => {
                  setBalanceIsRefreshing(false);
                  setDataLoaded(true);
                })
                .catch(() => {
                  setBalanceIsRefreshing(false);
                  setDataLoaded(true);
                });
            }, 1500);
          },
          isSubmitting: false,
          jobId,
        });
      },
    });
  };

  const openAddPaymentMethodModal = () => {
    openModal(ModalNamesEnum.AddPaymentMethod, {
      onCancel: onCancel,
      onSuccess: () => {
        openModal(ModalNamesEnum.PriceModal, {
          onCancel,
          isStripeLoading: false,
          setPaymentIntent,
          paymentIntent,
          onSuccess,
          isSubmitting: false,
          jobId,
        });
      },
    });
  };

  const loading = isSubmitting || !dataLoaded;

  let showContactBilling = false;
  let showContactPaymentMethod = false;
  let showContactBalance = false;
  let showNoBilling = false;
  let showNoPaymentMethod = false;
  let showNoBalance = false;
  if (!loading && (cannotManageBilling || cannotViewBilling)) {
    if (noBillingAccount) {
      showContactBilling = true;
    } else if (!paymentMethodId) {
      showContactPaymentMethod = true;
    } else if (notEnoughFunds) {
      showContactBalance = true;
    }
  } else if (!loading) {
    if (noBillingAccount) {
      showNoBilling = true;
    } else if (!paymentMethodId) {
      showNoPaymentMethod = true;
    } else if (notEnoughFunds) {
      showNoBalance = true;
    }
  }

  // If there is not enough available credit
  // AND the user does not have manage billing permissions
  // then display a message to ask the user to contact their organization administrators
  const contactAdminToSetBillingAccountMessage =
    'Please contact Organization Administrator to set up the Billing Account for the Organization';
  const contactAdminToTopUpBalanceMessage = 'Please contact Organization Administrator to top up the account balance';
  const contactAdminToSetPaymentMethodMessage = 'Please contact Organization Administrator to set up a payment method';

  const setBillingAccountMessage = (
    <Fragment>
      Please&nbsp;
      <ErrorClickableSpan onClick={() => navigate(routes.organizationDetails.make(organization?.slug ?? ''))}>
        set up the Billing Account
      </ErrorClickableSpan>
      &nbsp; for the Organization
    </Fragment>
  );
  const topUpBalanceMessage = (
    <Fragment>
      Your balance is insufficient.&nbsp;
      <ErrorClickableSpan onClick={() => openAddFundsModal()}>Add Funds</ErrorClickableSpan> to continue transcribing.
    </Fragment>
  );
  const setPaymentMethodMessage = (
    <Fragment>
      Please&nbsp;
      <ErrorClickableSpan onClick={() => openAddPaymentMethodModal()}>set up a payment method</ErrorClickableSpan>
    </Fragment>
  );

  if (showContactBilling) {
    return <GenericPageError message={contactAdminToSetBillingAccountMessage} />;
  }

  if (showContactBalance) {
    return <GenericPageError message={contactAdminToTopUpBalanceMessage} />;
  }

  if (showContactPaymentMethod && notEnoughFunds) {
    return <GenericPageError message={contactAdminToSetPaymentMethodMessage} />;
  }

  if (showNoBilling) {
    return <GenericPageError message={setBillingAccountMessage} />;
  }

  if (showNoBalance && !autoTopUpEnabled) {
    return <GenericPageError message={topUpBalanceMessage} />;
  }

  if (showNoBalance && autoTopUpEnabled) {
    return (
      <InfoWrapper>
        <InfoIcon className="info-icon" />
        Your balance will be automatically topped up by submitting this job.
      </InfoWrapper>
    );
  }

  // If there are enough funds we don't need to show an error message requiring the user to add a payment method
  // this may be the case if the user has a new account with a free credit balance
  if (showNoPaymentMethod && notEnoughFunds) {
    return <GenericPageError message={setPaymentMethodMessage} />;
  }

  return null;
}
